import { User } from '../types/user';
import IntegrationType, {
  IntegrationTypeLabel,
} from '../types/enums/integrationType';
import { Prospect } from '../types/prospect';
import { Account } from '../types/account';
import { AccountLinkedin } from 'crono-fe-common/types/accountLinkedin';
import { Constants } from 'crono-fe-common/constants/constants';
import { Event } from 'crono-fe-common/types/event';
import { ProspectLinkedin } from '../types/prospectLinkedin';
import { Opportunity } from '../types/DTO/opportunityViewResponseDTO';
import {
  HubspotSmIcon,
  SalesforceMdIcon,
  ResyncHubspotSmIcon,
  ResyncSalesforceSmIcon,
  ResyncPipedriveSmIcon,
} from '@crono-one/design-system';
import CrmAttioIcon from '../icons/Icon-CRM-Attio';
import ResyncAttioIcon from '../icons/Icon-Resync-Attio';
import ResyncZohoIcon from '../icons/Icon-Resync-Zoho';
import CrmZohoIcon from '../icons/Icon-CRM-Zoho';
import CrmDynamicsIcon from '../icons/Icon-CRM-Dynamics';
import ResyncDynamicsIcon from '../icons/Icon-Resync-Dynamics';
import { AccountForExport } from '../types/accountForExport';
import { ProspectForExport } from '../types/prospectForExport';
import { AttioSettings } from '../types/attioSettings';
import { ZohoRecordEntity, ZohoSettings } from '../types/zohoSettings';
import {
  DynamicsRecordEntity,
  DynamicsSettings,
} from '../types/dynamicsSettings';

export const isOnlyInCronoOptionVisible = (
  user: User | null | undefined,
): boolean => {
  return Boolean(
    user?.company?.integrationType &&
    user.company.integrationType !== IntegrationType.CRONO &&
    user.company.integrationType !== IntegrationType.CHART_MOGUL,
  );
};

function generateChartMogulAccountCrmLink(objectId?: string | null) {
  if (!objectId) return null;
  const cleanedObjectId = objectId.replace('cus_', '');
  return `https://app.chartmogul.com/#/customers/${cleanedObjectId}`;
}

const ATTIO_APP_BASE_URL = 'https://app.attio.com';

/**
 * Path the Attio app addresses a record by. People and companies have a path of
 * their own, while every other object — deals included — is reached through its
 * plural api slug and a `record` segment.
 */
const ATTIO_RECORD_PATHS = {
  company: 'company',
  person: 'person',
  deal: 'deals/record',
} as const;

type AttioObjectPath = keyof typeof ATTIO_RECORD_PATHS;

/**
 * Attio object ids are stored as `{workspaceId}_{recordId}`, while the app
 * addresses a record by its record id under the workspace slug.
 */
function generateAttioCrmLink(
  attioSettings: AttioSettings,
  objectPath: AttioObjectPath,
  objectId: string,
) {
  const separatorIndex = objectId.indexOf('_');
  const recordId =
    separatorIndex < 0 ? null : objectId.slice(separatorIndex + 1);

  if (!attioSettings.workspaceSlug || !recordId) return null;

  return `${ATTIO_APP_BASE_URL}/${attioSettings.workspaceSlug}/${ATTIO_RECORD_PATHS[objectPath]}/${recordId}`;
}

/**
 * Zoho object ids are stored as `{orgId}_{recordId}`. The web host and the
 * module segment of the url depend on the org's data center and come from
 * Zoho/Verify, so none of them is hardcoded here.
 */
function generateZohoCrmLink(
  zohoSettings: ZohoSettings,
  entity: ZohoRecordEntity,
  objectId: string,
) {
  const separatorIndex = objectId.indexOf('_');
  const recordId =
    separatorIndex < 0 ? null : objectId.slice(separatorIndex + 1);
  const segment = zohoSettings.recordUrlSegments?.[entity];

  if (
    !zohoSettings.webBaseUrl ||
    !zohoSettings.orgDomainName ||
    !segment ||
    !recordId
  )
    return null;

  return `${zohoSettings.webBaseUrl}/crm/${zohoSettings.orgDomainName}/tab/${segment}/${recordId}`;
}

/**
 * Dynamics object ids are stored as `{organizationId}_{recordId}`. The
 * environment host and the Dataverse table of each entity come from
 * Dynamics/Verify.
 */
function generateDynamicsCrmLink(
  dynamicsSettings: DynamicsSettings,
  entity: DynamicsRecordEntity,
  objectId: string,
) {
  const separatorIndex = objectId.indexOf('_');
  const recordId =
    separatorIndex < 0 ? null : objectId.slice(separatorIndex + 1);
  const table = dynamicsSettings.recordUrlSegments?.[entity];

  if (!dynamicsSettings.webUrl || !table || !recordId) return null;

  const webUrl = dynamicsSettings.webUrl.replace(/\/+$/, '');
  const app = dynamicsSettings.appId
    ? `appid=${encodeURIComponent(dynamicsSettings.appId)}&`
    : '';
  return `${webUrl}/main.aspx?${app}pagetype=entityrecord&etn=${table}&id=${recordId}`;
}

export interface GeneratedCrmLink {
  value: string | null;
  isSync: boolean;
}

export function generateProspectCrmLink(
  account: Pick<Account | AccountLinkedin, 'objectId'> | null,
  prospect: Pick<
    Prospect | ProspectLinkedin | ProspectForExport,
    'objectId' | 'isSynced' | 'fromContact'
  > | null,
  user: User | null,
): GeneratedCrmLink {
  let toRet: string | null = Constants.notInSyncDefaultMessage;

  if (
    !user ||
    (user.company?.integrationType === IntegrationType.CHART_MOGUL &&
      !account) ||
    !prospect
  )
    return { value: null, isSync: false };

  if (!prospect.isSynced || !prospect.objectId)
    return { value: Constants.notInSyncDefaultMessage, isSync: false };

  if (user.salesforceSettings !== null) {
    const objectId = prospect.objectId;
    const type = prospect.fromContact ? 'Contact' : 'Lead';
    toRet = `https://${user.salesforceSettings.domain}.lightning.force.com/lightning/r/${type}/${objectId}/view`;
  } else if (user.hubspotSettings !== null) {
    const objectId =
      prospect.objectId.split('_')[prospect.objectId.split('_').length - 1];
    toRet = `https://${user.hubspotSettings.domain}/contacts/${user.hubspotSettings.portalId}/contact/${objectId}`;
  } else if (user.pipedriveSettings !== null) {
    const objectId =
      prospect.objectId.split('_')[prospect.objectId.split('_').length - 1];
    toRet = `${user.pipedriveSettings.domain}/person/${objectId}`;
  } else if (user.attioSettings !== null) {
    toRet =
      generateAttioCrmLink(user.attioSettings, 'person', prospect.objectId) ??
      Constants.notInSyncDefaultMessage;
  } else if (user.zohoSettings) {
    toRet =
      generateZohoCrmLink(
        user.zohoSettings,
        prospect.fromContact ? 'prospect' : 'lead',
        prospect.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (user.dynamicsSettings) {
    toRet =
      generateDynamicsCrmLink(
        user.dynamicsSettings,
        prospect.fromContact ? 'prospect' : 'lead',
        prospect.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (
    account &&
    user.company?.integrationType === IntegrationType.CHART_MOGUL
  ) {
    toRet = generateChartMogulAccountCrmLink(account.objectId);
  }

  return { value: toRet, isSync: toRet !== Constants.notInSyncDefaultMessage };
}

export function generateAccountCrmLink(
  account: Pick<
    Omit<Account, 'prospects'> | AccountLinkedin | AccountForExport,
    'objectId' | 'isSynced'
  > | null,
  user: User | null,
): GeneratedCrmLink {
  let toRet: string | null = Constants.notInSyncDefaultMessage;

  if (!user || !account) return { value: null, isSync: false };

  if (!account.isSynced || !account.objectId)
    return { value: Constants.notInSyncDefaultMessage, isSync: false };

  if (user.salesforceSettings !== null) {
    const objectId = account.objectId;
    toRet = `https://${user.salesforceSettings.domain}.lightning.force.com/lightning/r/Account/${objectId}/view`;
  } else if (user.hubspotSettings !== null) {
    const objectId =
      account.objectId.split('_')[account.objectId.split('_').length - 1];
    toRet = `https://${user.hubspotSettings.domain}/contacts/${user.hubspotSettings.portalId}/company/${objectId}`;
  } else if (user.pipedriveSettings !== null) {
    const objectId =
      account.objectId.split('_')[account.objectId.split('_').length - 1];
    toRet = `${user.pipedriveSettings.domain}/organization/${objectId}`;
  } else if (user.attioSettings !== null) {
    toRet =
      generateAttioCrmLink(user.attioSettings, 'company', account.objectId) ??
      Constants.notInSyncDefaultMessage;
  } else if (user.zohoSettings) {
    toRet =
      generateZohoCrmLink(user.zohoSettings, 'account', account.objectId) ??
      Constants.notInSyncDefaultMessage;
  } else if (user.dynamicsSettings) {
    toRet =
      generateDynamicsCrmLink(
        user.dynamicsSettings,
        'account',
        account.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (user.company?.integrationType === IntegrationType.CHART_MOGUL) {
    toRet = generateChartMogulAccountCrmLink(account.objectId);
  }

  return { value: toRet, isSync: toRet !== Constants.notInSyncDefaultMessage };
}

export function generateOpportunityCrmLink(
  opportunity: Opportunity | null,
  user: User | null,
): GeneratedCrmLink {
  let toRet: string | null = Constants.notInSyncDefaultMessage;

  if (!user || !opportunity || opportunity.objectId == null)
    return { value: null, isSync: false };

  if (!opportunity.isSynced)
    return { value: Constants.notInSyncDefaultMessage, isSync: false };

  if (user.salesforceSettings !== null) {
    const objectId = opportunity.objectId;
    toRet = `https://${user.salesforceSettings.domain}.lightning.force.com/lightning/r/Opportunity/${objectId}/view`;
  } else if (user.hubspotSettings !== null) {
    const objectId =
      opportunity.objectId.split('_')[
        opportunity.objectId.split('_').length - 1
      ];
    toRet = `https://${user.hubspotSettings.domain}/contacts/${user.hubspotSettings.portalId}/deal/${objectId}`;
  } else if (user.pipedriveSettings !== null) {
    const objectId =
      opportunity.objectId.split('_')[
        opportunity.objectId.split('_').length - 1
      ];
    toRet = `${user.pipedriveSettings.domain}/deal/${objectId}`;
  } else if (user.attioSettings !== null) {
    toRet =
      generateAttioCrmLink(user.attioSettings, 'deal', opportunity.objectId) ??
      Constants.notInSyncDefaultMessage;
  } else if (user.zohoSettings) {
    toRet =
      generateZohoCrmLink(
        user.zohoSettings,
        'opportunity',
        opportunity.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (user.dynamicsSettings) {
    toRet =
      generateDynamicsCrmLink(
        user.dynamicsSettings,
        'opportunity',
        opportunity.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (user.company?.integrationType === IntegrationType.CHART_MOGUL) {
    toRet = generateChartMogulAccountCrmLink(opportunity.account?.objectId);
  }

  return { value: toRet, isSync: toRet !== Constants.notInSyncDefaultMessage };
}

export function generateEventCrmLink(
  event: Partial<Event> | null,
  user: User | null,
): GeneratedCrmLink {
  const isChartMogul =
    user?.company?.integrationType === IntegrationType.CHART_MOGUL;

  if (!event || !user || (isChartMogul && !event.account)) {
    return { value: null, isSync: false };
  }

  let toRet: string | null = Constants.notInSyncDefaultMessage;

  if (!event.isSynced || !event.objectId) {
    return { value: toRet, isSync: false };
  }

  if (user.salesforceSettings !== null) {
    const objectId = event.objectId;
    toRet = `https://${user.salesforceSettings.domain}.lightning.force.com/lightning/r/Task/${objectId}/view`;
  } else if (user.hubspotSettings !== null) {
    const objectIdParts = event.objectId.split('_');
    const objectId = objectIdParts[objectIdParts.length - 1];

    const prospectObjectIdParts = event.prospect?.objectId.split('_');
    const prospectObjectId = prospectObjectIdParts
      ? prospectObjectIdParts[prospectObjectIdParts.length - 1]
      : null;

    if (objectId && prospectObjectId) {
      const base = `https://${user.hubspotSettings.domain}/contacts/${user.hubspotSettings.portalId}`;
      const recordPath = 'record/0-1';
      const viewPath = 'view/1';
      toRet = `${base}/${recordPath}/${prospectObjectId}/${viewPath}?engagement=${objectId}`;
    }
  } else if (user.pipedriveSettings !== null) {
    // no link for event, fallback to prospect or account

    if (event.prospectId) {
      return generateProspectCrmLink(
        { objectId: event.account?.objectId ?? '' },
        event.prospect ?? null,
        user,
      );
    }

    if (event.accountId) {
      return generateAccountCrmLink(
        { objectId: event.account?.objectId ?? '', isSynced: !!event.isSynced },
        user,
      );
    }

    return { value: null, isSync: false };
  } else if (user.dynamicsSettings) {
    toRet =
      generateDynamicsCrmLink(
        user.dynamicsSettings,
        'meeting',
        event.objectId,
      ) ?? Constants.notInSyncDefaultMessage;
  } else if (event.account && isChartMogul) {
    // no link for event, fallback to account
    toRet = generateChartMogulAccountCrmLink(event.account.objectId);
  }

  return { value: toRet, isSync: toRet !== Constants.notInSyncDefaultMessage };
}

const LEAD_INTEGRATION_TYPES: ReadonlyArray<IntegrationType> = [
  IntegrationType.SALESFORCE,
  IntegrationType.ZOHO,
  IntegrationType.DYNAMICS,
];

/** Whether the CRM keeps leads apart from contacts, as Salesforce, Zoho and Dynamics do. */
export function doesCrmSupportLeads(integrationType?: IntegrationType | null) {
  return !!integrationType && LEAD_INTEGRATION_TYPES.includes(integrationType);
}

/**
 * Maps an integration type to its corresponding label
 * @param integrationType - The CRM integration type
 * @returns The appropriate label for the integration type, or a default value if not found
 */
export const getLabelForIntegrationType = (
  integrationType?: IntegrationType | null,
) => {
  switch (integrationType) {
    case IntegrationType.CRONO:
      return IntegrationTypeLabel.CRONO;
    case IntegrationType.SALESFORCE:
      return IntegrationTypeLabel.SALESFORCE;
    case IntegrationType.HUBSPOT:
      return IntegrationTypeLabel.HUBSPOT;
    case IntegrationType.PIPEDRIVE:
      return IntegrationTypeLabel.PIPEDRIVE;
    case IntegrationType.CHART_MOGUL:
      return IntegrationTypeLabel.CHART_MOGUL;
    case IntegrationType.ATTIO:
      return IntegrationTypeLabel.ATTIO;
    case IntegrationType.ZOHO:
      return IntegrationTypeLabel.ZOHO;
    case IntegrationType.DYNAMICS:
      return IntegrationTypeLabel.DYNAMICS;
    default:
      return 'CRM';
  }
};

/** Why a prospect whose company isn't synced to the CRM can only be added as a Lead. */
export const getLeadOnlyTooltip = (integrationType?: IntegrationType | null) =>
  `The company isn't synced with ${getLabelForIntegrationType(integrationType)}, so the contact can only be added as a Lead.`;

/** Why the prospects of a bulk import whose company isn't synced to the CRM are added as Leads. */
export const getBulkLeadOnlyTooltip = (
  integrationType?: IntegrationType | null,
) =>
  `The contacts whose company isn't synced with ${getLabelForIntegrationType(integrationType)} can only be added as Leads.`;

/**
 * Maps an integration type to its corresponding brand Tailwind class.
 * @param integrationType - The CRM integration type
 * @returns The appropriate brand class string for the integration type, or empty string if not found
 */
export function getBrandClassNameForIntegrationType(
  integrationType?: IntegrationType | null,
) {
  switch (integrationType) {
    case IntegrationType.HUBSPOT:
      return 'bg-hubspot';
    case IntegrationType.SALESFORCE:
      return 'bg-salesforce';
    case IntegrationType.PIPEDRIVE:
      return 'bg-pipedrive';
    case IntegrationType.ATTIO:
      return 'bg-[#202124]';
    default:
      return '';
  }
}

/**
 * Maps an integration type to its corresponding icon component.
 * @param integrationType - The CRM integration type
 * @returns The appropriate icon component for the integration type, or null if not found
 */
export function getIconForIntegrationType(
  integrationType?: IntegrationType | null,
) {
  switch (integrationType) {
    case IntegrationType.HUBSPOT:
      return HubspotSmIcon;
    case IntegrationType.SALESFORCE:
      return SalesforceMdIcon;
    case IntegrationType.ATTIO:
      return CrmAttioIcon;
    case IntegrationType.ZOHO:
      return CrmZohoIcon;
    case IntegrationType.DYNAMICS:
      return CrmDynamicsIcon;
    default:
      return null;
  }
}

/**
 * Maps an integration type to its corresponding resync icon component
 * @param integrationType - The CRM integration type
 * @returns The appropriate resync icon component for the integration type, or null if not found
 */
export function getResyncIconForIntegrationType(
  integrationType?: IntegrationType | null,
) {
  switch (integrationType) {
    case IntegrationType.HUBSPOT:
      return ResyncHubspotSmIcon;
    case IntegrationType.SALESFORCE:
      return ResyncSalesforceSmIcon;
    case IntegrationType.PIPEDRIVE:
      return ResyncPipedriveSmIcon;
    case IntegrationType.ATTIO:
      return ResyncAttioIcon;
    case IntegrationType.ZOHO:
      return ResyncZohoIcon;
    case IntegrationType.DYNAMICS:
      return ResyncDynamicsIcon;
    default:
      return null;
  }
}
